import { optional, optionalBoolean, optionalString, string, zodLess, ZodLessInfer } from '@kaiizen/zod-less';

/**
 * The three Kai-defined types. Financial rules belong to the type, never to the label, so a
 * rename never changes how a modifier behaves.
 */
export const TaskModifierTypeSchema = zodLess('resident|deferred|display');
export type TaskModifierType = ZodLessInfer<typeof TaskModifierTypeSchema>;

/**
 * Per-modifier org-settings record. Enablement = key presence in `taskSettings.modifiers`.
 *
 * Keys are arbitrary: an org creates its own Display modifiers (KAIDEV-3182), so a record's Kai
 * type can no longer be read off its key. `type` carries it instead. The two keys that predate
 * this — `deferred` and `tenantResponsibility` — are reserved, and their type is inferred when
 * absent so stored rows and existing clients keep working untouched.
 *
 * Runtime behavior (change orders, totals, reports) is driven by **line-item tag names**
 * (`deferred`, `tenantResponsibility`) in scan data — see `changeOrderLib.ts` (KAIDEV-4181).
 * Fields here are org-config / display metadata unless explicitly wired elsewhere.
 */
export const ApiTaskSettingsModifiersSchema = zodLess({
  modifierTag: string, // canonical line-item tag name; stable for the modifier's life, never derived from displayName
  type: optional(TaskModifierTypeSchema), // which Kai type's financial rules this follows; inferred for reserved keys
  displayName: optionalString, // kebab menu label override
  excludeFromPurchaseOrders: optionalBoolean, // nice to have to remind us how the rules work in the code, deferred only in defaults; doc-only until PO export wired
  triggeredByChangeOrder: optionalBoolean, // nice to have to remind us how the rules work in the code, absent or false = toggling this tag does not create a change order
  isEnabled: optionalBoolean, // whether the modifier is enabled for the org,absent = enabled (orgSettingsTradeLib convention)
  isEnabledFieldUser: optionalBoolean, // whether the modifier is enabled for field users, absent = enabled for field users
});
export type ApiTaskSettingsModifiers = ZodLessInfer<typeof ApiTaskSettingsModifiersSchema>;
